import { buildAuthedHeaders } from '@/lib/api-client'
import { env } from '@/lib/env'

export type ImageStyle = 'infografik' | 'whiteboard' | 'illustration' | 'hero'

export type ImageStreamEvent =
  | { type: 'status'; phase: string; detail?: string }
  | {
      type: 'done'
      vault_id: string
      vault_path: string
      storage_url?: string
      final_prompt?: string
      model?: string
    }
  | { type: 'error'; message: string }

export interface RunImageOptions {
  prompt: string
  style?: ImageStyle
  brand?: string
  primaryBrandId?: string | null
  sessionId?: string
  signal?: AbortSignal
  onEvent: (event: ImageStreamEvent) => void
}

export async function streamImage(opts: RunImageOptions): Promise<void> {
  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')

  const response = await fetch(`${baseUrl}/api/image`, {
    method: 'POST',
    headers: await buildAuthedHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    }),
    body: JSON.stringify({
      prompt: opts.prompt,
      style: opts.style,
      brand: opts.brand,
      primary_brand_id: opts.primaryBrandId ?? undefined,
      session_id: opts.sessionId,
    }),
    signal: opts.signal,
  })

  if (!response.ok) {
    let message = response.statusText
    try {
      const body = (await response.json()) as { error?: { message?: string } }
      message = body.error?.message ?? message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }
  if (!response.body) throw new Error('Keine Stream-Antwort')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let separatorIdx = buffer.indexOf('\n\n')
      while (separatorIdx !== -1) {
        const rawEvent = buffer.slice(0, separatorIdx)
        buffer = buffer.slice(separatorIdx + 2)
        for (const line of rawEvent.split('\n')) {
          if (!line.startsWith('data:')) continue
          const payload = line.slice(5).trim()
          if (!payload) continue
          try {
            const parsed = JSON.parse(payload) as ImageStreamEvent
            opts.onEvent(parsed)
          } catch (err) {
            console.error('SSE parse error', err, payload)
          }
        }
        separatorIdx = buffer.indexOf('\n\n')
      }
    }
  } finally {
    reader.releaseLock()
  }
}
